import React,{useEffect,useMemo,useState} from 'react';
import {httpsCallable} from 'firebase/functions';
import Hero from '../../components/Hero';
import SectionCard from '../../components/SectionCard';
import DataTable from '../../components/DataTable';
import {SearchableSelect,TextAreaField,TextField} from '../../components/FormControls';
import {functions} from '../../firebase';
import {useAuth} from '../../contexts/AuthContext';
import {useSettings} from '../../contexts/SettingsContext';
import {useToast} from '../../contexts/ToastContext';
import {subscribeTenantCollection} from '../../services/crud';
import {ALL_COMMUNICATION_EVENTS,COMMUNICATION_EVENT_GROUPS,DEFAULT_EVENT_CHANNELS} from '../../config/communicationEvents';

const yesNo=[{value:'on',label:'Enabled'},{value:'off',label:'Disabled'}];
const recipientOptions=[
  {value:'student',label:'Student only'},
  {value:'parent',label:'Parent / Guardian only'},
  {value:'both',label:'Student + Parent / Guardian'},
];
const eventDefaults=(row={})=>({...DEFAULT_EVENT_CHANNELS,...row});

export default function CommunicationAutomationPage(){
  const {tenantId,can}=useAuth();
  const {settings}=useSettings();
  const {notify}=useToast();
  const [policies,setPolicies]=useState({});
  const [selected,setSelected]=useState(ALL_COMMUNICATION_EVENTS[0]?.key||'admission.submitted');
  const [saving,setSaving]=useState(false);
  const [logs,setLogs]=useState([]);
  const [filter,setFilter]=useState('all');

  useEffect(()=>{
    setPolicies(settings.communicationEvents||{});
  },[settings.communicationEvents]);
  useEffect(()=>subscribeTenantCollection('communicationEvents',tenantId,setLogs,()=>{}),[tenantId]);

  const meta=ALL_COMMUNICATION_EVENTS.find(x=>x.key===selected)||ALL_COMMUNICATION_EVENTS[0];
  const current=eventDefaults(policies[selected]||{});
  const rows=useMemo(()=>ALL_COMMUNICATION_EVENTS.map(event=>{
    const p=eventDefaults(policies[event.key]||{});
    return {
      id:event.key,event:event.label,group:event.group,
      email:p.email?'ON':'OFF',sms:p.sms?'ON':'OFF',whatsapp:p.whatsapp?'ON':'OFF',inApp:p.inApp?'ON':'OFF',secureMessaging:p.secureMessaging?'ON':'OFF',
      recipients:p.parent&&p.student?'Student + Parent':p.parent?'Parent':p.student?'Student':'None',
    };
  }),[policies]);
  const visibleLogs=filter==='all'?logs:logs.filter(x=>x.eventKey===filter);

  function patch(next){setPolicies(prev=>({...prev,[selected]:{...eventDefaults(prev[selected]||{}),...next}}));}
  async function save(){
    setSaving(true);
    try{
      const result=await httpsCallable(functions,'saveCommunicationEventPolicies')({policies});
      setPolicies(result.data?.policies||policies);
      notify('Communication event policies saved.','success');
    }catch(error){notify(error.message||'Unable to save communication automation.','error')}
    finally{setSaving(false)}
  }

  return <>
    <Hero eyebrow="EVENT-DRIVEN COMMUNICATION" title="Communication Automation" subtitle="Configure exactly which system events send Email, SMS, WhatsApp API, in-app notifications or secure internal messages. Every delivery originates from one event ID to prevent duplicate sends." chips={[settings.academicYear||'Current Session','Duplicate-safe events','Student / Parent targeting']} stats={[
      {icon:'✉️',label:'EMAIL EVENTS',value:rows.filter(x=>x.email==='ON').length},
      {icon:'📲',label:'SMS EVENTS',value:rows.filter(x=>x.sms==='ON').length},
      {icon:'🟢',label:'WHATSAPP EVENTS',value:rows.filter(x=>x.whatsapp==='ON').length},
      {icon:'🔔',label:'IN-APP EVENTS',value:rows.filter(x=>x.inApp==='ON').length},
    ]}/>

    <div className="communication-policy-layout">
      <SectionCard icon="⚙️" title="Event Policy" subtitle="Select an ERP event and choose channels, recipients, cooldown and optional message overrides.">
        <SearchableSelect label="System Event" value={selected} onChange={setSelected} options={ALL_COMMUNICATION_EVENTS.map(x=>({value:x.key,label:x.label,description:`${x.group} · ${x.description}`}))}/>
        {meta&&<div className="policy-note" style={{marginTop:12}}><strong>{meta.label}</strong><div>{meta.description}</div></div>}
        <div className="settings-form-grid" style={{marginTop:14}}>
          <SearchableSelect label="Email" value={current.email?'on':'off'} onChange={v=>patch({email:v==='on'})} options={yesNo}/>
          <SearchableSelect label="SMS Gateway" value={current.sms?'on':'off'} onChange={v=>patch({sms:v==='on'})} options={yesNo}/>
          <SearchableSelect label="WhatsApp API" value={current.whatsapp?'on':'off'} onChange={v=>patch({whatsapp:v==='on'})} options={yesNo}/>
          <SearchableSelect label="In-App Notification" value={current.inApp?'on':'off'} onChange={v=>patch({inApp:v==='on'})} options={yesNo}/>
          <SearchableSelect label="Secure Internal Message" value={current.secureMessaging?'on':'off'} onChange={v=>patch({secureMessaging:v==='on'})} options={yesNo}/>
          <SearchableSelect label="Recipients" value={current.parent&&current.student?'both':current.parent?'parent':'student'} onChange={v=>patch({parent:v==='parent'||v==='both',student:v==='student'||v==='both'})} options={recipientOptions}/>
          <TextField label="Cooldown (Minutes)" type="number" min="0" value={current.cooldownMinutes??0} onChange={e=>patch({cooldownMinutes:Number(e.target.value||0)})} help="Useful for fee reminders during coaching attendance."/>
          <TextField label="Template Key Override" value={current.templateKey||''} onChange={e=>patch({templateKey:e.target.value})} placeholder={selected}/>
          <TextAreaField className="span-2" label="Optional Message Override" value={current.messageTemplate||''} onChange={e=>patch({messageTemplate:e.target.value})} help="Leave blank to use the configured Email/SMS/WhatsApp template. Variables such as {{studentName}}, {{amount}}, {{dueDate}}, {{instituteName}} remain supported."/>
        </div>
        {can('communication.edit')&&<div className="form-actions"><button className="button button-primary" disabled={saving} onClick={save}>{saving?'Saving…':'Save Event Policies'}</button></div>}
      </SectionCard>
      <SectionCard icon="🧭" title="Policy Matrix" subtitle="A quick audit of the configured channels for every important ERP event.">
        <DataTable rows={rows} columns={['group','event','email','sms','whatsapp','inApp','secureMessaging','recipients']} pageSize={25}/>
      </SectionCard>
    </div>

    <SectionCard icon="📡" title="Communication Event History" subtitle="One canonical event can fan out into Email, SMS, WhatsApp, in-app and secure-message deliveries without duplicate business events.">
      <div style={{maxWidth:420,marginBottom:12}}><SearchableSelect label="Filter Event" value={filter} onChange={setFilter} options={[{value:'all',label:'All Events'},...ALL_COMMUNICATION_EVENTS.map(x=>({value:x.key,label:x.label}))]}/></div>
      <DataTable rows={visibleLogs} columns={['createdAt','eventKey','eventId','recipientName','studentName','status','sourceId']} exportEnabled pageSize={50}/>
    </SectionCard>
  </>;
}
